import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    UserPlus,
    Copy,
    Trash2,
    ShieldCheck,
    Clock,
    XCircle,
    Check,
    Info,
    Crown,
    Users,
    GraduationCap,
    Search,
    X,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import ThemedSelect from "../../components/ThemedSelect";
import { useConfirm } from "../../components/ConfirmDialog";
import { useAuth } from "../../contexts/AuthContext";

const ROLE_OPTIONS_ADMIN = [
    { value: "learner", label: "Apprenant·e" },
    { value: "manager", label: "Manager" },
    { value: "admin", label: "Administrateur·rice" },
];
const ROLE_OPTIONS_MANAGER = [
    { value: "learner", label: "Apprenant·e" },
];

const ROLE_LABELS = {
    learner: "Apprenant·e",
    manager: "Manager",
    admin: "Administrateur·rice",
    candidate: "Candidat·e",
};

const STATUS_FILTERS = [
    { value: "all", label: "Toutes" },
    { value: "pending", label: "En attente" },
    { value: "used", label: "Utilisées" },
    { value: "expired", label: "Expirées" },
    { value: "revoked", label: "Révoquées" },
];

const ROLE_ICON = {
    admin: Crown,
    manager: Users,
    learner: GraduationCap,
};

function inviteUrl(token) {
    return `${window.location.origin}/register?invite=${token}`;
}

function statusBadge(status) {
    switch (status) {
        case "pending":
            return { label: "En attente", cls: "bg-shariti-yellow/20 text-shariti-yellow", icon: Clock };
        case "used":
            return { label: "Utilisée", cls: "bg-emerald-500/20 text-emerald-400", icon: Check };
        case "expired":
            return { label: "Expirée", cls: "bg-white/10 text-white/40", icon: XCircle };
        case "revoked":
            return { label: "Révoquée", cls: "bg-red-500/20 text-red-400", icon: XCircle };
        default:
            return { label: status, cls: "bg-white/10 text-white/50", icon: Info };
    }
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (e) {
        return "—";
    }
}

// Iter-30.46 — Format court mobile : "15 juin · 20:27" (sans année si année
// actuelle). Lecture rapide sur écran étroit, et la `<p>` ne se découpe plus
// en un mot par ligne quand l'espace dispo est faible.
function formatDateShort(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const now = new Date();
        const sameYear = d.getFullYear() === now.getFullYear();
        const datePart = d.toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
            ...(sameYear ? {} : { year: "numeric" }),
        });
        const timePart = d.toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
        });
        return `${datePart} · ${timePart}`;
    } catch (e) {
        return "—";
    }
}

export default function AdminInvitations() {
    const { user: currentUser } = useAuth();
    const confirm = useConfirm();
    const isAdmin = currentUser?.role === "admin";
    const roleOptions = isAdmin ? ROLE_OPTIONS_ADMIN : ROLE_OPTIONS_MANAGER;

    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    // Iter-30.47 — Recherche live + compteurs par statut
    const [search, setSearch] = useState("");

    // Create form
    const [form, setForm] = useState({
        role: "learner",
        name: "",
        email: "",
        expires_in_days: 7,
    });
    const [creating, setCreating] = useState(false);
    const [lastCreated, setLastCreated] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/invitations");
            setInvites(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const create = async (e) => {
        e?.preventDefault();
        setCreating(true);
        try {
            const { data } = await api.post("/invitations", {
                role: form.role,
                name: form.name || undefined,
                email: form.email || undefined,
                expires_in_days: Number(form.expires_in_days) || 7,
            });
            setInvites((prev) => [data, ...prev]);
            setLastCreated(data);
            setForm({ role: "learner", name: "", email: "", expires_in_days: 7 });
            toast.success("Invitation créée — copie le lien et envoie-le");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
        } finally {
            setCreating(false);
        }
    };

    const copyLink = async (token) => {
        const url = inviteUrl(token);
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Lien copié dans le presse-papier");
        } catch (e) {
            // Fallback for environments without clipboard API
            const ta = document.createElement("textarea");
            ta.value = url;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
            toast.success("Lien copié");
        }
    };

    const revoke = async (invite) => {
        const ok = await confirm({
            title: "Révoquer cette invitation ?",
            description:
                "Le lien deviendra immédiatement invalide. Cette action est définitive.",
            confirmLabel: "Révoquer",
            tone: "danger",
        });
        if (!ok) return;
        try {
            const { data } = await api.delete(`/invitations/${invite.invite_id}`);
            setInvites((prev) =>
                prev.map((i) => (i.invite_id === invite.invite_id ? data : i)),
            );
            toast.success("Invitation révoquée");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
        }
    };

    // Iter-30.47 — Filtrage en 2 passes : 1) statut (onglet), 2) recherche
    // textuelle live sur nom / email / créateur / utilisateur. useMemo pour
    // éviter de recalculer à chaque render (101 invitations × 5 champs).
    const counts = useMemo(() => {
        const c = { all: invites.length, pending: 0, used: 0, expired: 0, revoked: 0 };
        invites.forEach((i) => {
            c[i.status] = (c[i.status] || 0) + 1;
        });
        return c;
    }, [invites]);

    const filtered = useMemo(() => {
        const byStatus =
            filter === "all" ? invites : invites.filter((i) => i.status === filter);
        const q = search.trim().toLowerCase();
        if (!q) return byStatus;
        return byStatus.filter((i) => {
            const haystack = [
                i.name,
                i.email,
                i.created_by_name,
                i.used_by_name,
                i.token,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [invites, filter, search]);

    return (
        <div className="space-y-6" data-testid="admin-invitations">
            <div>
                <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                    Accès sur invitation uniquement
                </p>
                <h2 className="text-2xl font-extrabold text-white flex items-center gap-2">
                    <UserPlus className="w-6 h-6 text-shariti-orange-light" />
                    Invitations
                </h2>
                <p className="text-sm text-white/60 mt-1">
                    Génère un lien unique, copie-le et envoie-le par WhatsApp, SMS ou email.
                </p>
            </div>

            {/* Create form */}
            <motion.form
                onSubmit={create}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-3xl p-5 lg:p-6 space-y-4"
                data-testid="invitation-create-form"
            >
                <div className="grid md:grid-cols-4 gap-3">
                    <div className="md:col-span-1">
                        <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                            Rôle
                        </label>
                        <ThemedSelect
                            value={form.role}
                            onChange={(v) => setForm({ ...form, role: v })}
                            options={roleOptions}
                            dataTestid="invitation-role-select"
                        />
                    </div>
                    <div className="md:col-span-1">
                        <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                            Nom (optionnel)
                        </label>
                        <input
                            type="text"
                            placeholder="Ex : Yanis Recrut"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="invitation-name-input"
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
                        />
                    </div>
                    <div className="md:col-span-1">
                        <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                            Réserver à un email <span className="opacity-60">(optionnel)</span>
                        </label>
                        <input
                            type="email"
                            placeholder="Optionnel"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            data-testid="invitation-email-input"
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
                        />
                        <p className="text-[10px] text-white/40 mt-1.5 leading-snug">
                            {form.email
                                ? "Seule cette adresse pourra utiliser le lien — plus sûr."
                                : "Lien ouvert : qui a le lien peut s'inscrire (à usage unique)."}
                        </p>
                    </div>
                    <div className="md:col-span-1">
                        <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                            Expire dans (jours)
                        </label>
                        <input
                            type="number"
                            min={1}
                            max={90}
                            value={form.expires_in_days}
                            onChange={(e) =>
                                setForm({ ...form, expires_in_days: e.target.value })
                            }
                            data-testid="invitation-expires-input"
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white outline-none focus:border-shariti-orange/60"
                        />
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 pt-2">
                    <button
                        type="submit"
                        disabled={creating}
                        data-testid="invitation-create-button"
                        className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold py-3 px-5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60"
                    >
                        <UserPlus className="w-4 h-4" />
                        {creating ? "Création..." : "Générer le lien"}
                    </button>
                    <div className="flex items-start gap-2 text-xs text-white/50 flex-1">
                        <Info className="w-3.5 h-3.5 mt-0.5 text-shariti-orange-light shrink-0" />
                        <span>
                            Le lien est à <strong className="text-white/80">usage unique</strong>. Si tu remplis l'email,
                            <strong className="text-white/80"> seule cette adresse pourra l'utiliser</strong> —
                            sinon n'importe qui ayant le lien pourra s'inscrire.
                        </span>
                    </div>
                </div>

                {lastCreated && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="rounded-2xl p-4 bg-shariti-orange/10 border border-shariti-orange/30 flex flex-wrap items-center gap-3"
                        data-testid="invitation-last-created"
                    >
                        <ShieldCheck className="w-5 h-5 text-shariti-orange-light" />
                        <div className="min-w-0 flex-1">
                            <p className="text-xs font-thin tracking-widest uppercase text-shariti-orange-light">
                                Lien prêt
                            </p>
                            <p className="text-sm text-white font-mono truncate">
                                {inviteUrl(lastCreated.token)}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => copyLink(lastCreated.token)}
                            data-testid="invitation-copy-last"
                            className="inline-flex items-center gap-1.5 bg-shariti-orange text-white font-extrabold text-xs uppercase tracking-widest px-3 py-2 rounded-xl hover:bg-shariti-orange-light transition-all"
                        >
                            <Copy className="w-3.5 h-3.5" /> Copier
                        </button>
                    </motion.div>
                )}
            </motion.form>

            {/* Filter row */}
            <div
                className="overflow-x-auto scrollbar-hide -mx-1 px-1"
                data-testid="invitation-filters-scroll"
            >
                <div
                    className="flex gap-2 p-1.5 glass-card rounded-full w-max"
                    data-testid="invitation-filters"
                >
                    {STATUS_FILTERS.map((f) => {
                        const count = counts[f.value] ?? 0;
                        return (
                            <button
                                key={f.value}
                                onClick={() => setFilter(f.value)}
                                data-testid={`invitation-filter-${f.value}`}
                                className={`px-3 sm:px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all inline-flex items-center gap-1.5 ${
                                    filter === f.value
                                        ? "bg-shariti-orange text-white"
                                        : "text-white/60 hover:text-white"
                                }`}
                            >
                                {f.label}
                                <span
                                    className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded-full ${
                                        filter === f.value
                                            ? "bg-black/25 text-white"
                                            : "bg-white/8 text-white/60"
                                    }`}
                                >
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Iter-30.47 — Recherche live sur la liste filtrée par statut */}
            <div className="relative" data-testid="invitation-search-wrap">
                <Search className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher par nom, email, créateur, utilisateur…"
                    data-testid="invitation-search-input"
                    className="w-full bg-white/5 border border-white/10 focus:border-shariti-orange/50 focus:bg-white/8 text-sm text-white placeholder-white/35 rounded-2xl pl-10 pr-10 py-2.5 transition-all outline-none"
                />
                {search && (
                    <button
                        onClick={() => setSearch("")}
                        data-testid="invitation-search-clear"
                        aria-label="Effacer la recherche"
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg bg-white/5 hover:bg-white/15 text-white/55 hover:text-white transition-all"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            {/* List */}
            {loading ? (
                <div className="h-32 glass-card rounded-3xl animate-pulse" />
            ) : filtered.length === 0 ? (
                <div className="glass-card rounded-3xl p-8 text-center" data-testid="invitations-empty">
                    <UserPlus className="w-10 h-10 text-white/30 mx-auto mb-3" />
                    <p className="text-white/60">
                        {search.trim()
                            ? `Aucune invitation ne correspond à « ${search.trim()} »`
                            : "Aucune invitation pour ce filtre."}
                    </p>
                    {search.trim() && (
                        <button
                            onClick={() => setSearch("")}
                            className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-shariti-orange-light hover:text-shariti-orange"
                            data-testid="invitations-empty-clear-search"
                        >
                            <X className="w-3 h-3" /> Effacer la recherche
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map((inv) => {
                        const badge = statusBadge(inv.status);
                        const RoleIcon = ROLE_ICON[inv.role] || GraduationCap;
                        const StatusIcon = badge.icon;
                        return (
                            <motion.div
                                key={inv.invite_id}
                                layout
                                className="glass-card rounded-2xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
                                data-testid={`invitation-row-${inv.invite_id}`}
                            >
                                {/* Iter-30.46 — Mobile : header row = icon + nom +
                                    badges sur la même ligne (badges wrap au besoin
                                    avec whitespace-nowrap). Boutons d'action en
                                    pleine largeur sous la card. */}
                                <div className="flex items-start gap-3 sm:contents min-w-0">
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                                        <RoleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-shariti-orange-light" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-extrabold text-white truncate text-sm sm:text-base">
                                            {inv.name || "Invitation générique"}
                                        </p>
                                        <div className="flex items-center gap-1.5 flex-wrap mt-1">
                                            <span
                                                className={`text-[9px] sm:text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full whitespace-nowrap ${badge.cls} inline-flex items-center gap-1`}
                                            >
                                                <StatusIcon className="w-3 h-3 shrink-0" />
                                                {badge.label}
                                            </span>
                                            <span className="text-[9px] sm:text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/5 text-white/60 whitespace-nowrap">
                                                {ROLE_LABELS[inv.role] || inv.role}
                                            </span>
                                            {inv.kind === "cooptation" && (
                                                <span
                                                    className="text-[9px] sm:text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-shariti-orange/15 text-shariti-orange-light whitespace-nowrap"
                                                    title="Lien de parrainage généré par un apprenant — durée fixe de 30 jours"
                                                >
                                                    Cooptation
                                                </span>
                                            )}
                                            {inv.email ? (
                                                <span
                                                    className="text-[9px] sm:text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 whitespace-nowrap"
                                                    title={`Réservé à ${inv.email}`}
                                                >
                                                    Réservée
                                                </span>
                                            ) : (
                                                <span
                                                    className="text-[9px] sm:text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-shariti-yellow/20 text-shariti-yellow whitespace-nowrap"
                                                    title="N'importe qui ayant le lien peut l'utiliser"
                                                >
                                                    Lien ouvert
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-[11px] sm:text-xs text-white/50 mt-1.5 truncate">
                                            {inv.email
                                                ? `Réservée à ${inv.email}`
                                                : "Aucun email · n'importe qui peut s'inscrire"}
                                        </p>
                                        <p className="text-[11px] sm:text-xs text-white/40 mt-0.5 truncate">
                                            Créée le {formatDateShort(inv.created_at)} ·{" "}
                                            {inv.created_by_name}
                                        </p>
                                        <p className="text-[11px] sm:text-xs text-white/45 mt-0.5 truncate">
                                            {inv.status === "used"
                                                ? `Utilisée par ${inv.used_by_name} le ${formatDateShort(inv.used_at)}`
                                                : inv.status === "revoked"
                                                  ? `Révoquée le ${formatDateShort(inv.revoked_at)}`
                                                  : `Expire le ${formatDateShort(inv.expires_at)}`}
                                        </p>
                                    </div>
                                </div>
                                {/* Boutons d'action : pleine largeur mobile, inline desktop */}
                                {inv.status === "pending" && (
                                    <div className="flex gap-2 shrink-0 sm:ml-auto">
                                        <button
                                            onClick={() => copyLink(inv.token)}
                                            data-testid={`invitation-copy-${inv.invite_id}`}
                                            className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 bg-white/5 hover:bg-shariti-orange/20 border border-white/10 hover:border-shariti-orange/40 text-white font-bold text-xs uppercase tracking-widest px-3 py-2.5 rounded-xl transition-all"
                                        >
                                            <Copy className="w-3.5 h-3.5" /> Copier
                                        </button>
                                        <button
                                            onClick={() => revoke(inv)}
                                            data-testid={`invitation-revoke-${inv.invite_id}`}
                                            className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/40 text-white/70 hover:text-red-400 font-bold text-xs uppercase tracking-widest px-3 py-2.5 rounded-xl transition-all"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" /> Révoquer
                                        </button>
                                    </div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
